'use client';

import { useEffect, useRef } from 'react';
import debounce from '@/lib/debounce';

export function useCollapseEqualHeight() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = ref.current as HTMLDivElement;
        if (!container) {
            return;
        }
        const controller = new AbortController();
        let containerWidth = container.offsetWidth;

        function initialiseTriggers() {
            const triggers = container.querySelectorAll<HTMLInputElement>('[data-group-name] input[type=checkbox]');
            for (const trigger of triggers) {
                const groupName = trigger.closest('[data-group-name]')?.getAttribute('data-group-name');
                if (!groupName) {
                    continue;
                }
                trigger.addEventListener(
                    'change',
                    (event) => {
                        const trigger = event.target as HTMLInputElement;
                        const relatedTriggers = container.querySelectorAll<HTMLInputElement>(`[data-group-name="${groupName}"] input[type=checkbox]`);
                        for (const relatedTrigger of relatedTriggers) {
                            relatedTrigger.checked = trigger.checked;
                        }
                    },
                    { signal: controller.signal }
                );
            }
        }

        initialiseGroupHeights(container);
        initialiseTriggers();

        const observer = new MutationObserver(() => {
            initialiseGroupHeights(container);
            initialiseTriggers();
        });
        observer.observe(container, { childList: true, subtree: true });

        const handleResize: ResizeObserverCallback = debounce((entries) => {
            const targetWidth = entries[0].contentRect.width;
            if (containerWidth === targetWidth) {
                return;
            }
            containerWidth = targetWidth;
            initialiseGroupHeights(container);
        }, 150);
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);
        return () => {
            controller.abort();
            observer.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    return ref;
}

function initialiseGroupHeights(container: HTMLDivElement) {
    const groupContainers = container.querySelectorAll<HTMLDivElement>('[data-group-name]');
    const groupNames = new Set(Array.from(groupContainers).map((groupEl) => groupEl.getAttribute('data-group-name')));
    const heights = new Map<string, number>();
    const groupNodes = new Map<string, NodeListOf<HTMLDivElement>>();

    for (const groupName of groupNames) {
        if (!groupName) {
            continue;
        }
        const relatedNodes = container.querySelectorAll<HTMLDivElement>(`[data-group-name="${groupName}"] .collapse-content .collapse-equal-height`);
        groupNodes.set(groupName, relatedNodes);
        for (const relatedNode of relatedNodes) {
            relatedNode?.style.removeProperty('--equal-height');
            heights.set(groupName, Math.max(heights.get(groupName) || 0, relatedNode.offsetHeight));
        }
    }
    for (const [groupName, height] of heights) {
        const relatedNodes = groupNodes.get(groupName);
        if (!relatedNodes) {
            continue;
        }
        for (const group of relatedNodes) {
            group.style.setProperty('--equal-height', `${height}px`);
        }
    }
}
